import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/SharedAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCreateTicket } from '@/hooks/useTickets';
import { calculatePriority, FORM_ASSIGNMENTS } from '../../../../../shared/form-config';
import { useTranslation } from 'react-i18next';
import { ReporterInfoFields } from '@/components/tickets/ReporterInfoFields';
import { TicketFormButtons } from '@/components/tickets/TicketFormButtons';

const hardwareRequestSchema = z.object({
  summary: z.string().min(5, 'El resumen debe tener al menos 5 caracteres'),
  team: z.string().min(1, 'El equipo es requerido'),
  role: z.string().min(1, 'El rol es requerido'),
  changeReason: z.enum(['breakage', 'loss', 'performance', 'other'], {
    required_error: 'Debe seleccionar un motivo',
  }),
  priority: z.enum(['high', 'medium', 'low'], {
    required_error: 'Debe seleccionar una prioridad',
  }),
});

type HardwareRequestFormData = z.infer<typeof hardwareRequestSchema>;

export function HardwareRequestForm() {
  const { user } = useAuth();
  const { t } = useTranslation();

  const { register, handleSubmit, formState: { errors }, control } = useForm<HardwareRequestFormData>({
    resolver: zodResolver(hardwareRequestSchema),
  });

  const createTicketMutation = useCreateTicket();

  const onSubmit = async (data: HardwareRequestFormData) => {
    const priority = calculatePriority('hardware-request', data);
    const ticketData = {
      type: 'hardware-request',
      title: `Hardware Request: ${data.summary}`,
      formData: {
        reporterName: user?.name,
        reporterEmail: user?.email,
        ...data,
      },
      attachments: [],
      priority,
      assignedTo: FORM_ASSIGNMENTS['hardware-request'],
    };
    createTicketMutation.mutate(ticketData);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('forms.hardwareRequest')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Reporter info (auto-filled) */}
          <ReporterInfoFields name={user?.name} email={user?.email} />

          {/* Summary */}
          <div className="space-y-2">
            <Label htmlFor="summary">
              {t('forms.summary')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="summary"
              placeholder={t('forms.summaryPlaceholder')}
              {...register('summary')}
            />
            {errors.summary && (
              <p className="text-sm text-destructive">{errors.summary.message}</p>
            )}
          </div>

          {/* Team and Role */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="team">
                {t('forms.team')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="team"
                {...register('team')}
              />
              {errors.team && (
                <p className="text-sm text-destructive">{errors.team.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">
                {t('forms.role')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="role"
                {...register('role')}
              />
              {errors.role && (
                <p className="text-sm text-destructive">{errors.role.message}</p>
              )}
            </div>
          </div>

          {/* Change Reason */}
          <div className="space-y-2">
            <Label>
              {t('forms.changeReason')} <span className="text-destructive">*</span>
            </Label>
            <Controller
              name="changeReason"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="breakage">{t('forms.reasonBreakage')}</SelectItem>
                    <SelectItem value="loss">{t('forms.reasonLossTheft')}</SelectItem>
                    <SelectItem value="performance">{t('forms.reasonPerformance')}</SelectItem>
                    <SelectItem value="other">{t('forms.reasonOther')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.changeReason && (
              <p className="text-sm text-destructive">{errors.changeReason.message}</p>
            )}
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label>
              {t('forms.priority')} <span className="text-destructive">*</span>
            </Label>
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">{t('forms.priorityHigh')}</SelectItem>
                    <SelectItem value="medium">{t('forms.priorityMedium')}</SelectItem>
                    <SelectItem value="low">{t('forms.priorityLow')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.priority && (
              <p className="text-sm text-destructive">{errors.priority.message}</p>
            )}
          </div>

          {/* Buttons */}
          <TicketFormButtons isPending={createTicketMutation.isPending} />
        </form>
      </CardContent>
    </Card>
  );
}
